import { z } from 'zod';
import type { OperatorActionType } from './operator-action';

/**
 * OUTBOUND agency-task LIFECYCLE — Coordination Re-Arch Phase 3b-2a (#379, Ruling 2;
 * docs/coordination-rearchitecture-council-2026-06.md §H.2/§H.3/§H.4). The pure,
 * server-and-client-shared core of the down-tasking state machine: the status
 * vocabulary, the valid from→to edges, who may drive each edge (the actor guard), and
 * the scribe verb each edge stamps. It holds NO DB / IO — the API routes wrap these
 * helpers around `withAgencyContext` + explicit two-party agency filters; the web
 * renders the allowed actions from `agencyTaskActionsFor`.
 *
 * The flow: the PEOC (REQUESTER) ISSUEs a task DOWN to a NAMED, NON-SELF agency (the
 * TARGET). The target acknowledges, then accepts (or declines) it; on accept the target
 * starts work then completes it. The requester may cancel any time pre-completion.
 * declined / completed / cancelled are terminal.
 *
 *   issued ─acknowledge(target)→ acknowledged ─accept(target)→ accepted ─start(target)→ in_progress
 *     │  │                            │                                                      │
 *     │  └accept(target)→ accepted    └decline(target)→ declined (terminal)   ─complete(target)→ completed
 *     │  └decline(target)→ declined
 *     └──── cancel(requester) from {issued, acknowledged, accepted, in_progress} → cancelled
 *
 * 3b-2a builds only the REQUESTER side (issue + cancel); 3b-2b wires the TARGET-side
 * response handlers (acknowledge / accept / decline / start / complete) + the message
 * thread. The full edge set is defined here so the machine compiles in one place. The
 * DB `task_status` column is free-text (the drone-state convention); these literals ARE
 * the controlled vocabulary. Pinned by `agency-task.test.ts`.
 */

/** Every agency-task status. Initial is 'issued' (the migration-0101 column default). */
export const AGENCY_TASK_STATUSES = [
  'issued',
  'acknowledged',
  'accepted',
  'declined',
  'in_progress',
  'completed',
  'cancelled',
] as const;

export const AgencyTaskStatusSchema = z.enum(AGENCY_TASK_STATUSES);
export type AgencyTaskStatus = z.infer<typeof AgencyTaskStatusSchema>;

/** Terminal states — no further transition is possible from these. */
export const TERMINAL_AGENCY_TASK_STATUSES = ['declined', 'completed', 'cancelled'] as const;
export function isTerminalAgencyTaskStatus(status: AgencyTaskStatus): boolean {
  return (TERMINAL_AGENCY_TASK_STATUSES as readonly string[]).includes(status);
}

/** Urgency vocabulary (mirrors the DB CHECK on agency_tasks.urgency). */
export const AGENCY_TASK_URGENCIES = ['routine', 'urgent', 'immediate'] as const;
export const AgencyTaskUrgencySchema = z.enum(AGENCY_TASK_URGENCIES);
export type AgencyTaskUrgency = z.infer<typeof AgencyTaskUrgencySchema>;

/**
 * The two parties to a task. The actor guard is enforced in the APPLICATION (the route
 * loads the task filtered by the acting agency's party column, then checks the rule's
 * actor here), NOT just by the two-party RLS — the single FOR-ALL `agency_tasks` policy
 * cannot express per-party write restrictions (the same #190 gap as drone_missions).
 *   - requester — the PEOC/originator agency that ISSUED the task (requesting_agency_id).
 *   - target    — the tasked agency (target_agency_id).
 */
export type AgencyTaskActorRole = 'requester' | 'target';

/** The post-issue transition actions (issue = creation, handled separately — like the
 *  drone 'request' verb). 3b-2a uses only `cancel`; 3b-2b wires the target-side rest. */
export const AGENCY_TASK_ACTIONS = [
  'acknowledge',
  'accept',
  'decline',
  'start',
  'complete',
  'cancel',
] as const;
export const AgencyTaskActionSchema = z.enum(AGENCY_TASK_ACTIONS);
export type AgencyTaskAction = z.infer<typeof AgencyTaskActionSchema>;

export interface AgencyTaskTransitionRule {
  /** Valid source states for this action. */
  readonly from: readonly AgencyTaskStatus[];
  /** The resulting state. */
  readonly to: AgencyTaskStatus;
  /** The ONLY party allowed to drive this action. */
  readonly actor: AgencyTaskActorRole;
  /** The operator-action scribe verb this transition stamps (FK-lookup, migration 0101). */
  readonly verb: OperatorActionType;
}

/**
 * The agency-task state machine — the single source of truth for valid edges, actor,
 * and verb. Typing `verb` as `OperatorActionType` makes the lockstep a COMPILE-TIME
 * guarantee: a verb missing from `OPERATOR_ACTION_TYPES` (migration 0101) fails the
 * build here.
 */
export const AGENCY_TASK_TRANSITIONS: Record<AgencyTaskAction, AgencyTaskTransitionRule> = {
  acknowledge: {
    from: ['issued'],
    to: 'acknowledged',
    actor: 'target',
    verb: 'acknowledge_agency_task',
  },
  accept: {
    from: ['issued', 'acknowledged'],
    to: 'accepted',
    actor: 'target',
    verb: 'accept_agency_task',
  },
  decline: {
    from: ['issued', 'acknowledged'],
    to: 'declined',
    actor: 'target',
    verb: 'decline_agency_task',
  },
  start: { from: ['accepted'], to: 'in_progress', actor: 'target', verb: 'start_agency_task' },
  complete: {
    from: ['in_progress'],
    to: 'completed',
    actor: 'target',
    verb: 'complete_agency_task',
  },
  cancel: {
    // The requester may cancel ANY time pre-completion.
    from: ['issued', 'acknowledged', 'accepted', 'in_progress'],
    to: 'cancelled',
    actor: 'requester',
    verb: 'cancel_agency_task',
  },
};

/** The scribe verb stamped on task ISSUE (the requester raises it). */
export const AGENCY_TASK_ISSUED_VERB: OperatorActionType = 'issue_agency_task';

/** The agency-task scribe verbs (lockstep with migration 0101): the issue verb + the one
 *  verb each transition stamps. Migration 0101 seeds all 7. */
export const AGENCY_TASK_VERBS: readonly OperatorActionType[] = [
  AGENCY_TASK_ISSUED_VERB,
  ...AGENCY_TASK_ACTIONS.map((a) => AGENCY_TASK_TRANSITIONS[a].verb),
];

/** Which party (if any) the given agency plays on a task. `null` ⇒ not a party. */
export function agencyTaskActorRole(
  task: { requestingAgencyId: string; targetAgencyId: string },
  agencyId: string,
): AgencyTaskActorRole | null {
  if (task.requestingAgencyId === agencyId) return 'requester';
  if (task.targetAgencyId === agencyId) return 'target';
  return null;
}

export type AgencyTaskTransitionResult =
  | { ok: true; to: AgencyTaskStatus; verb: OperatorActionType }
  | { ok: false; reason: 'invalid_transition' | 'forbidden_actor' };

/**
 * Validate a transition end-to-end: the actor must be the party the rule allows, AND the
 * task must currently sit in a valid source state. Returns the target state + the scribe
 * verb on success. `forbidden_actor` is reported before `invalid_transition` so a
 * wrong-party attempt never leaks the task's current state.
 */
export function evaluateAgencyTaskTransition(
  action: AgencyTaskAction,
  fromStatus: AgencyTaskStatus,
  actorRole: AgencyTaskActorRole,
): AgencyTaskTransitionResult {
  const rule = AGENCY_TASK_TRANSITIONS[action];
  if (rule.actor !== actorRole) return { ok: false, reason: 'forbidden_actor' };
  if (!rule.from.includes(fromStatus)) return { ok: false, reason: 'invalid_transition' };
  return { ok: true, to: rule.to, verb: rule.verb };
}

/** The actions a given party may attempt (regardless of state) — for surfacing UI controls. */
export function agencyTaskActionsFor(role: AgencyTaskActorRole): AgencyTaskAction[] {
  return AGENCY_TASK_ACTIONS.filter((a) => AGENCY_TASK_TRANSITIONS[a].actor === role);
}

/** The actions a party may attempt RIGHT NOW (valid actor AND valid source state). */
export function availableAgencyTaskActions(
  fromStatus: AgencyTaskStatus,
  role: AgencyTaskActorRole,
): AgencyTaskAction[] {
  return agencyTaskActionsFor(role).filter((a) =>
    AGENCY_TASK_TRANSITIONS[a].from.includes(fromStatus),
  );
}

/**
 * An agency the operator can task DOWN — the shape `GET /api/agencies` returns for the
 * AgencyPicker. `kind` is the agency's jurisdiction tier (a member of AGENCY_KINDS), kept
 * as a plain string here so the read-view stays decoupled from enum-completeness.
 */
export const AgencyViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
});
export type AgencyView = z.infer<typeof AgencyViewSchema>;

/**
 * The wire shape of an agency_task as the requester-side API serializes it. The target's
 * name is joined for display; the link ids thread back to the inbound assistance request,
 * the COP incident, the raised resource_request, and/or the originating dispatch rec.
 * Lifecycle stamps are ISO strings (or null until reached).
 */
export const AgencyTaskViewSchema = z.object({
  id: z.string(),
  task_number: z.string(),
  requesting_agency_id: z.string(),
  target_agency_id: z.string(),
  target_agency_name: z.string().nullable(),
  requesting_agency_name: z.string().nullable(),
  narrative: z.string(),
  urgency: z.string(),
  response_level: z.number().nullable(),
  task_status: AgencyTaskStatusSchema,
  assistance_request_id: z.string().nullable(),
  incident_id: z.string().nullable(),
  resource_request_id: z.string().nullable(),
  dispatch_recommendation_id: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
  declined_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  declined_reason: z.string().nullable(),
  completion_notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AgencyTaskView = z.infer<typeof AgencyTaskViewSchema>;
